
import React from "react";
import { Button } from "@/components/ui/button";
import { Edit, ChevronRight } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";

interface AccountHeaderProps {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  profileImageUrl?: string | null;
  onEditProfile?: () => void;
}

const AccountHeader = ({ 
  firstName, 
  lastName, 
  email, 
  profileImageUrl, 
  onEditProfile 
}: AccountHeaderProps) => {
  const displayName = firstName || lastName ? 
    `${firstName || ''} ${lastName || ''}`.trim() : 
    email;
  
  // Get initials for avatar fallback
  const initials = displayName ? 
    displayName.split(' ')
      .map(n => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase() 
    : '?';

  return (
    <div className="mb-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span>Dashboard</span>
          <ChevronRight className="h-4 w-4" />
          <span className="font-medium text-[#4A607B]">My Account</span>
        </div>
        
        {onEditProfile && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onEditProfile}
            className="flex items-center gap-1 self-end sm:self-auto"
          >
            <Edit className="h-4 w-4" />
            <span>Edit Profile</span>
          </Button>
        )}
      </div>

      <div className="bg-white rounded-lg p-6 shadow">
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
          <Avatar className="h-20 w-20 border-2 border-gray-200">
            {profileImageUrl ? (
              <AvatarImage src={profileImageUrl} alt={displayName || 'User'} />
            ) : null}
            <AvatarFallback className="text-xl bg-[#E5DEFF] text-[#6E59A5]">{initials}</AvatarFallback>
          </Avatar>
          
          <div className="text-center sm:text-left">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-1">
              Welcome back, {displayName}
            </h2>
            <p className="text-gray-600 max-w-2xl">
              Here you can monitor your active strategies, manage your subscription, and track your referrals.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountHeader;
